import { defineStore } from 'pinia'
import ApiService from '@/core/services/ApiService'
import { User, type UserFeatures } from '@/modules/user/user/models/user'

export interface LoginCredentials {
  email: string
  password: string
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    loading: false,
    errors: {},
    user: {} as User,
    isAuthenticated: false,
    isCheckingAuth: false,
    lastAuthCheck: null as number | null,
    authCheckInterval: 5 * 60 * 1000 // 5 minutes
  }),
  getters: {
    userHasRole: (state) => {
      return (roleName) => state.user.roles?.some((role) => role.name === roleName) || false
    },
    userHasPermission: (state) => {
      return (model, action, type: string | null = null) =>
        state.user.permissions?.some(
          (permission) =>
            permission.model === model &&
            permission.action === action &&
            (type === null || permission.type === type)
        ) || false
    },
    userHasFeatureEnabled: (state) => {
      return (feature: keyof UserFeatures) => state.user.features?.[feature] === true
    },
    isAuthExpired: (state) => {
      if (!state.lastAuthCheck) return true
      return Date.now() - state.lastAuthCheck > state.authCheckInterval
    }
  },
  actions: {
    setAuth(authUser: User) {
      this.isAuthenticated = true
      this.user = authUser
      this.errors = {}
      this.lastAuthCheck = Date.now()
    },
    setCheckingAuth(checking: boolean) {
      this.isCheckingAuth = checking
    },
    setError(error: any) {
      this.errors = { ...error }
    },
    purgeAuth() {
      this.isAuthenticated = false
      this.user = {} as User
      this.errors = {}
      this.lastAuthCheck = null
    },
    async login(credentials: LoginCredentials, remember: boolean = false) {
      return await ApiService.get('/sanctum/csrf-cookie')
        .then(async () => {
          await ApiService.post('/login', { ...credentials, remember })
            .then(async () => {
              await ApiService.get('/api/users/me')
                .then(async (data) => {
                  this.setAuth(data.data.data)
                })
                .catch((error) => {
                  this.setError(error.response.data.errors)
                  console.error(error.response.data.errors)
                })
            })
            .catch((error) => {
              this.setError(error.response.data.errors)
              console.error(error.response.data.errors)
            })
        })
        .catch((error) => {
          this.setError(error.response.data.errors)
          console.error(error.response.data.errors)
        })
    },
    async logout() {
      try {
        // Make a request to the server to invalidate the user's session
        await ApiService.post('/logout', {})
        // Clear the user data from the client-side store
        this.purgeAuth()
      } catch (error) {
        console.error(error)
      }
    },
    async forgotPassword(payload: { email: string }) {
      return ApiService.get('/sanctum/csrf-cookie')
        .then(() => {
          return ApiService.post('/forgot-password', payload)
        })
        .then(() => {
          this.setError({})
        })
        .catch(({ error }) => {
          this.setError(error.response.data.errors)
        })
    },
    async resetPassword(payload: {
      email: string
      password: string
      password_confirmation: string
      token: string
    }) {
      return ApiService.get('/sanctum/csrf-cookie')
        .then(() => {
          return ApiService.post('/reset-password', payload)
        })
        .then(() => {
          this.setError({})
        })
        .catch(({ error }) => {
          this.setError(error.response.data.errors)
        })
    }
  }
})
